import { Button, LockIcon, Space, Table, Text } from '@/components';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { useProjectsPageSearchQueryStates } from '@/features/project/_hooks/search-projects/search-query-states';
import { useUpdateGroupTargets } from '@/features/project/groups/_hooks/group';
import type { GroupProjectListModel } from '@/features/project/groups/group-detail/_models/group';
import { useId } from '@/hooks';
import { t } from '@/i18n';
import { PAGE_SIZE_IN_BACKEND, lowerCamelStringToSnake } from '@/utils';
import { ExportOutlined } from '@ant-design/icons/lib/icons';
import type { FC } from 'react';
import type { z } from 'zod';

type Props = {
  groupInProjects: z.infer<typeof GroupProjectListModel> | undefined;
  isLoading: boolean;
  editPermission: boolean;
};

export const GroupInProjectsTable: FC<Props> = ({
  groupInProjects,
  isLoading,
  editPermission,
}) => {
  const { id } = useId();
  const { searchQueryStates, setSearchQueryStates } =
    useProjectsPageSearchQueryStates();
  const { mutateAsync: updateGroupTargetsAsync } = useUpdateGroupTargets(id);
  const groupInProjectIds =
    groupInProjects?.results.map((groupInProject) => groupInProject.id) ?? [];

  return (
    <Table
      rowKey="id"
      dataSource={groupInProjects?.results}
      loading={isLoading}
      columns={[
        {
          title: t('project'),
          key: 'name',
          render: (_, record) => {
            return (
              <Space>
                {record.isLocked && <LockIcon hint={t('locked-project')} />}
                <Text weight="regular" text={record.name} />
              </Space>
            );
          },
          defaultSortOrder: getSortOrder({
            currentSortOrderKey: searchQueryStates.groupInProjectsOrdering,
            targetKey: 'name',
          }),
          sorter: true,
        },
        {
          title: t('fiscal-year'),
          key: 'fiscalYear',
          render: (_, record) => record.fiscalYear,
        },
        {
          title: '',
          key: 'actions',
          width: editPermission ? 160 : 80,
          render: (_, record) => (
            <Space>
              {editPermission && (
                <ProjectResourceGuard onlyEdit>
                  <Button
                    style={{ width: 75 }}
                    onClick={async () => {
                      await updateGroupTargetsAsync(
                        groupInProjectIds.filter((id) => id !== record.id),
                      );
                    }}
                  >
                    {t('group-project-operation-release-project')}
                  </Button>
                </ProjectResourceGuard>
              )}
              <Button
                type="link"
                icon={<ExportOutlined aria-label="" />}
                onClick={() => {
                  window.open(`/rs/project/${record.id}`, '_blank');
                }}
              >
                {t('details')}
              </Button>
            </Space>
          ),
        },
      ]}
      pagination={{
        current: Number(searchQueryStates.groupInProjectsPage),
        total: groupInProjects?.count,
        showSizeChanger: false,
        pageSize: PAGE_SIZE_IN_BACKEND,
      }}
      onChange={(pagination, _filters, sorter) => {
        const newSortOrderKey =
          !Array.isArray(sorter) && sorter.order && sorter.columnKey
            ? `${sorter.order === 'descend' ? '-' : ''}${lowerCamelStringToSnake(sorter.columnKey.toString())}`
            : null;
        setSearchQueryStates((prev) => ({
          ...prev,
          groupInProjectsPage: pagination.current ?? 1,
          groupInProjectsOrdering: newSortOrderKey,
        }));
      }}
    />
  );
};

const getSortOrder = ({
  currentSortOrderKey,
  targetKey,
}: { currentSortOrderKey: string | null; targetKey: string }):
  | 'ascend'
  | 'descend'
  | undefined => {
  const sortingKey: string = lowerCamelStringToSnake(targetKey);
  if (currentSortOrderKey === sortingKey) {
    return 'ascend';
  }
  if (currentSortOrderKey === `-${sortingKey}`) {
    return 'descend';
  }
  return undefined;
};
